import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { getUnique } from '@fmnts/core/collection';
import { createPathGetter } from '@fmnts/core/object';
import { distinctUntilFormKeysChanged } from '@fmnts/core/rxjs';
import { map, OperatorFunction } from 'rxjs';
import {
  ConditionDefinition,
  isAtomicCondition,
  isConditionAnd,
  isConditionEquals,
  isConditionOr,
} from './conditions';
import { resolveConditionFn } from './resolve-condition';

interface ValueProvider {
  get(path: string | (string | number)[]): { value: unknown };
}

function createValueProvider(obj: any): ValueProvider {
  const getByPath = createPathGetter(obj, '.');

  return {
    get: (path) => ({ value: getByPath(path) }),
  };
}

/**
 * Service for checking conditions on values, controls, form groups and objects.
 */
@Injectable({
  providedIn: 'root',
})
export class ConditionalService {
  /**
   * @param control The control, e.g. FormControl, FormArray, FormGroup, ...
   * @param condition Condition object
   *
   * @returns
   * The boolean result of checking the control against the condition.
   * If the `control` doesn't exist or no check applies, `undefined` is returned.
   */
  public checkGroup(
    control: AbstractControl,
    condition: ConditionDefinition,
  ): boolean | undefined {
    return this.fulfills(control as ValueProvider, condition);
  }

  /**
   * @param obj Object with values
   * @param condition Condition object
   *
   * @returns
   * The boolean result of the corresponding condition check.
   * If no check applies, `undefined` is returned.
   */
  public checkObj(
    obj: { [k: string]: any } | unknown,
    condition: ConditionDefinition,
  ): boolean | undefined {
    return this.fulfills(createValueProvider(obj), condition);
  }

  /**
   * @param provider Object with values
   * @param conditions Condition objects
   *
   * @returns
   * `true`, if all conditions are fulfilled, otherwise `false`.
   */
  private fulfillsEvery(
    provider: ValueProvider,
    conditions: ConditionDefinition[],
  ): boolean {
    return conditions.every((condition) => this.fulfills(provider, condition));
  }

  /**
   * @param conditions List of Conditions
   *
   * @returns
   * `true` if one of the Control Validation returns true,
   * `false` if none evaluates to true.
   */
  private fulfillsSome(
    provider: ValueProvider,
    conditions: ConditionDefinition[],
  ): boolean {
    return conditions.some((condition) => this.fulfills(provider, condition));
  }

  /**
   * @param provider Value provider
   * @param condition Condition object
   *
   * @returns
   * The result of the corresponding condition check. If no
   * condition check applies, `undefined` is returned.
   *
   * // TODO: Could be refactored to only returning a boolean and
   * throw errors for all other cases.
   */
  public fulfills(
    provider: ValueProvider,
    condition: ConditionDefinition,
  ): boolean | undefined {
    // Check if we are dealing with an invalid condition object
    if (typeof condition !== 'object' || condition === null) {
      return undefined;
    }

    if (isConditionOr(condition)) {
      return this.fulfillsSome(provider, condition.or);
    }

    if (isConditionAnd(condition)) {
      return this.fulfillsEvery(provider, condition.and);
    }

    // Get the value that is referenced by key
    if (typeof provider !== 'object' || provider === null) {
      return undefined;
    }

    if (isAtomicCondition(condition)) {
      const control = provider.get(condition.key);
      if (!control) {
        // Log a warning here for sentry
        console.warn(`Invalid condition: unknown field '${condition.key}'`);
        return undefined;
      }

      // Get the actual check function and call it with the value
      const conditionFn = this.resolveConditionFn(condition);
      if (conditionFn) {
        return conditionFn(control.value);
      }
    }

    return undefined;
  }

  /**
   * @param condition Condition from which keys should be extracted
   *
   * @returns
   * Array of referenced keys in the given `condition`
   */
  public extractReferencedKeys(condition: ConditionDefinition): string[] {
    if (typeof condition !== 'object' || condition === null) {
      return [];
    }

    // In case we are dealing with an connective type condition, extract keys recursively
    const childConditions = isConditionOr(condition)
      ? condition.or
      : isConditionAnd(condition)
        ? condition.and
        : undefined;

    if (childConditions) {
      return childConditions.flatMap((c) => this.extractReferencedKeys(c));
    }

    return 'key' in condition ? [condition.key] : [];
  }

  /**
   * @param condition Condition which the emitted value is checked against
   *
   * @returns
   * Emits a boolean value when a source observable emits a value, which is
   * `true` if the emitted value fulfills the given `condition`
   *
   * @example
   * of({test: 0}, {test: 1}).pipe(
   *  service.fulfills$({key: 'test', equals: 1})
   * );
   * // -> emits `false`, then `true`
   */
  public fulfills$(
    condition: ConditionDefinition,
  ): OperatorFunction<Record<string, unknown>, boolean | undefined> {
    const keys = getUnique(this.extractReferencedKeys(condition));

    return (obs$) =>
      obs$.pipe(distinctUntilFormKeysChanged(keys), this.checkObj$(condition));
  }

  private checkObj$(condition: ConditionDefinition) {
    return map((value) => this.checkObj(value, condition));
  }

  /**
   * Resolves a `condition` object to a condition function.
   *
   * @param condition Condition definition
   *
   * @returns
   * Condition function
   */
  private resolveConditionFn(condition: Record<string, any>) {
    // For now we ensure to use this condition without type safety
    if (isConditionEquals(condition)) {
      condition = { ...condition, typeSafe: false };
    }

    return resolveConditionFn(condition);
  }
}
